import { useState, useEffect, useCallback } from 'react';
import { authenticateWithWallet, checkAuthentication } from '../services/auth';

// Add session storage key for better persistence
const USER_SESSION_KEY = 'radcoon_authenticated_user';

/**
 * Hook for managing authentication state
 * @param {Object} walletInfo - Information about the user's wallet from useRadixConnect
 * @returns {Object} Auth state and methods
 */
export function useAuth(walletInfo = {}) {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { connected, address } = walletInfo;
  
  // Try to load user from sessionStorage on initialization
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem(USER_SESSION_KEY);
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.log('Loaded user from session storage:', parsedUser);
        setUser(parsedUser);
        setUserId(parsedUser.id);
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Error loading user from session storage:', e);
    }
  }, []);
  
  // Authenticate with wallet
  const login = useCallback(async (walletAddress) => {
    if (!walletAddress) {
      console.error('Login attempted with no wallet address');
      setError('No wallet address provided');
      setIsLoading(false);
      return null;
    }
    
    console.log('Attempting login with wallet:', walletAddress);
    setIsLoading(true);
    setError(null);
    
    try {
      // Authenticate with Supabase
      const { user, error, isNewUser } = await authenticateWithWallet(walletAddress);
      
      if (error) {
        console.error('Authentication error:', error);
        setError(error.message || 'Authentication failed');
        setIsLoading(false);
        return null;
      }
      
      // Save user in state
      setUser(user);
      setUserId(user.id);
      
      // Save user in session storage for persistence
      try {
        sessionStorage.setItem(USER_SESSION_KEY, JSON.stringify(user));
      } catch (e) {
        console.error('Error saving user to session storage:', e);
      }
      
      console.log('Authentication successful. User ID:', user.id, 'Is new user:', isNewUser);
      
      setIsLoading(false);
      return user;
    } catch (e) {
      console.error('Unexpected error during authentication:', e);
      setError(e.message || 'Unexpected error during authentication');
      setIsLoading(false);
      return null;
    }
  }, []);
  
  // Logout function
  const logout = useCallback(() => {
    setUser(null);
    setUserId(null);
    setError(null);
  }, []);
  
  // Check session on mount and when wallet connection changes
  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true);
      
      // First check localStorage for session
      const storedUserId = localStorage.getItem('radcoon_user_id');
      
      if (storedUserId) {
        const { authenticated, user, error } = await checkAuthentication(storedUserId);
        
        if (authenticated && user) {
          setUser(user);
          setUserId(user.id);
          setIsLoading(false);
          return;
        }
        
        // Clear invalid session
        localStorage.removeItem('radcoon_user_id');
      }
      
      // If we're connected with a wallet, try to authenticate
      if (connected && address) {
        login(address);
      } else {
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [connected, address, login]);
  
  // Store user ID in localStorage when it changes
  useEffect(() => {
    if (userId) {
      localStorage.setItem('radcoon_user_id', userId);
    } else {
      localStorage.removeItem('radcoon_user_id');
    }
  }, [userId]);
  
  return {
    user,
    userId,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: !!user
  };
} 